import { Track } from "@/src/lib/tracking";
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
} from "react";
import { Icon, VirtualList } from "@ospweb/sdk-ui";
import { useSnapshot } from "valtio";
import PaymentHistory from "./PaymentHistory";
import SubscriptionAction from "./SubscriptionAction";
import { SubscriptionList } from "./SubscriptionList";
import type { ManageBillingModalContentProps } from "./types";
import Translations from "@translate/components/Member/CurrentPlan/ManageBillingModal/ManageBillingModalContent";
import { useGetListPayment } from "@/src/hooks/useGetListPayment";
import { usePaymentHistory } from "@/src/hooks/usePaymentHistory";
import { I18nUtil } from "@utils/i18n";
import { rootStore } from "@store/rootStore";
import { useSubscriptionList } from "@/src/hooks/useSubscriptionList";
import { useStartPaymentMethodUpdate } from "@hooks/useSubscriptionManage";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 管理账单弹窗内容区（无 Modal 外壳，供 EdgenModalCreateAble 使用）
 */
const ManageBillingModalContent: FC<ManageBillingModalContentProps> = ({
  onClose,
  view,
  allowBack = true,
  onCancelSubscription,
  position = "pc_modal",
}) => {
  const { ownerInfo } = useSnapshot(rootStore.user);
  const [step, setStep] = useState(view === "subscriptionAction" ? 1 : 0);
  // const [subscriptionListItems, setSubscriptionListItems] = useState<
  //   SubscriptionListItem[]
  // >([]);
  const { getListPayment, cancelSubscription } = useGetListPayment();
  const { data: subscriptionListItems } = useSubscriptionList({
    enabled: !!ownerInfo?.id,
  });
  const { start: startPaymentMethodUpdate } = useStartPaymentMethodUpdate();
  const getListPaymentRef = useRef(getListPayment);
  const isFetchingNextPageRef = useRef(false);
  getListPaymentRef.current = getListPayment;

  const {
    data: paymentHistory,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
  } = usePaymentHistory({
    params: {
      owner_id: ownerInfo?.id,
      limit: 10,
    },
    enabled: !!ownerInfo?.id,
  });

  const handleEndReached = useCallback(() => {
    if (
      !hasNextPage ||
      isFetching ||
      isLoading ||
      isFetchingNextPageRef.current
    ) {
      return;
    }
    isFetchingNextPageRef.current = true;
    void fetchNextPage().finally(() => {
      isFetchingNextPageRef.current = false;
    });
  }, [fetchNextPage, hasNextPage, isFetching, isLoading]);

  useEffect(() => {
    if (!isFetching) {
      isFetchingNextPageRef.current = false;
    }
  }, [isFetching]);

  const shouldShowSubscriptionAction = useMemo(() => {
    return (
      Array.isArray(subscriptionListItems) &&
      subscriptionListItems.length > 0 &&
      step === 0
    );
  }, [subscriptionListItems, step]);

  return (
    <div className="p-4 md:p-6">
      <Icon
        data-openreplay-label="member_managebillingmodalcontent_close"
        name="close"
        size={24}
        className="text-content_primary absolute top-3 right-3 cursor-pointer z-10 button_icon"
        onClick={onClose}
      />
      {step === 0 && (
        <Fragment>
          <div className="text-content_primary headline_regular_m w-full">
            {I18n.t("Manage Billing")}
          </div>
          <VirtualList
            hasNextPage={hasNextPage}
            onEndReached={handleEndReached}
            onEndReachedThreshold="120"
            renderLoading={!!fetchNextPage && isFetching}
            renderNoMore={!hasNextPage && !isLoading}
            noMoreContainerClassName="h-20"
            className="block max-h-[412px] min-h-[240px] overflow-y-auto no-scrollbar"
          >
            <div className="flex flex-col">
              <PaymentHistory paymentHistory={paymentHistory || []} />
              {shouldShowSubscriptionAction && (
                <SubscriptionAction
                  onCancelSubscription={() => {
                    Track.log("info", "member", "-[cancelSubscription]");
                    setStep(step + 1);
                  }}
                />
              )}
            </div>
          </VirtualList>
        </Fragment>
      )}
      {step === 1 && (
        <Fragment>
          <div className="flex items-center gap-1">
            {allowBack && (
              <Icon
                data-openreplay-label="member_managebillingmodalcontent_leftarrowalt"
                name="left-arrow-alt"
                size={24}
                className="text-content_primary cursor-pointer button_icon"
                onClick={() => setStep(step - 1)}
              />
            )}
            <div>
              <div className="text-content_primary headline_regular_s md:headline_regular_m">
                {I18n.t("Manage Subscription")}
              </div>
              <div className="text-content_tertiary md:text-content_secondary body_m">
                {I18n.t("Display only auto-renewing subscriptions")}
              </div>
            </div>
          </div>
          <SubscriptionList
            items={subscriptionListItems}
            onManageSubscription={cancelSubscription}
            onCancel={onCancelSubscription}
            onUpdatePaymentMethod={(item) => {
              void startPaymentMethodUpdate(item);
            }}
            position={position}
          />
        </Fragment>
      )}
    </div>
  );
};

export default memo(ManageBillingModalContent);
